import React, { useState } from "react";
import { Bell, Filter, X, RefreshCcw } from "lucide-react";

const Alerts = () => {
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Sample data
  const alerts = [
    {
      id: 1,
      platform: "Platform A",
      module: "SACP Module 3",
      message: "Critical: Potential below threshold (-0.49V)",
      details:
        "Channel 1 reading consistently below protection threshold for >30 minutes",
      severity: "critical",
      status: "open",
      timestamp: "2024-02-16T10:23:00",
      assignee: "John Smith",
      actions: [
        { time: "2024-02-16T10:23:00", action: "Alert created" },
        { time: "2024-02-16T10:24:00", action: "Notification sent to team" },
      ],
    },
    {
      id: 2,
      platform: "Platform B",
      module: "SACP Module 7",
      message: "Warning: Potential approaching lower limit (-0.72V)",
      details: "Channel 2 showing downward trend over past 2 hours",
      severity: "warning",
      status: "investigating",
      timestamp: "2024-02-16T09:45:00",
      assignee: "Sarah Johnson",
      actions: [
        { time: "2024-02-16T09:45:00", action: "Alert created" },
        { time: "2024-02-16T09:50:00", action: "Engineer assigned" },
      ],
    },
  ];

  const getSeverityStyle = (severity) => {
    const styles = {
      critical: "bg-red-50 border-red-200",
      warning: "bg-yellow-50 border-yellow-200",
      info: "bg-blue-50 border-blue-200",
    };
    return styles[severity] || "bg-gray-50 border-gray-200";
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: "bg-red-100 text-red-800",
      investigating: "bg-yellow-100 text-yellow-800",
      resolved: "bg-green-100 text-green-800",
    };
    return badges[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">System Alerts</h1>
          <p className="text-gray-600">Monitor and manage system alerts</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Filter size={16} />
            Filters
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold">Filter Alerts</h2>
            <button onClick={() => setShowFilters(false)}>
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-4">
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Platforms</option>
              <option value="Platform A">Platform A</option>
              <option value="Platform B">Platform B</option>
            </select>
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div className="space-y-4">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`border rounded-lg ${getSeverityStyle(
              alert.severity
            )} p-4`}
          >
            <div className="flex justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Bell
                    size={16}
                    className={
                      alert.severity === "critical"
                        ? "text-red-500"
                        : "text-yellow-500"
                    }
                  />
                  <span className="font-semibold">{alert.module}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(
                      alert.status
                    )}`}
                  >
                    {alert.status}
                  </span>
                </div>
                <p className="text-gray-600">{alert.message}</p>
                <p className="text-sm text-gray-500">{alert.details}</p>
              </div>
              <div className="text-right space-y-2">
                <p className="text-sm text-gray-500">
                  {new Date(alert.timestamp).toLocaleString()}
                </p>
                <p className="text-sm">Assigned to: {alert.assignee}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold mb-2">Alert Timeline</h4>
              <div className="space-y-2">
                {alert.actions.map((action, index) => (
                  <div key={index} className="flex gap-2 text-sm">
                    <span className="text-gray-500">
                      {new Date(action.time).toLocaleTimeString()}
                    </span>
                    <span>{action.action}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Alerts;
